import type {
  BillingInterval,
  SubscriptionDetails,
} from '@/services/api/billing/useGetBillingSubscription'
import type { PortalAction } from '@/services/api/billing/useBillingSessions'

/**
 * What the owner asked the Stripe portal to do, remembered across the
 * redirect so the billing page can wait until Stripe has applied it instead
 * of showing the old plan on return.
 */
export interface PortalExpectation {
  teamId: string
  action: PortalAction
  planKey?: string
  interval?: BillingInterval
  startedAt: number
}

const STORAGE_KEY = 'pf_portal_expectation'
const MAX_AGE_MS = 60 * 60 * 1000

export function rememberPortalAction(
  expectation: Omit<PortalExpectation, 'startedAt'>,
): void {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...expectation, startedAt: Date.now() }),
    )
  } catch {
    // storage unavailable — the page falls back to a single refresh
  }
}

/** Read and clear the remembered action for this space, if still fresh. */
export function takePortalExpectation(
  teamId: string | number | null | undefined,
): PortalExpectation | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    sessionStorage.removeItem(STORAGE_KEY)
    if (!raw) return null

    const expectation = JSON.parse(raw) as PortalExpectation
    const fresh = Date.now() - expectation.startedAt < MAX_AGE_MS
    return fresh && String(expectation.teamId) === String(teamId)
      ? expectation
      : null
  } catch {
    return null
  }
}

/** Has Stripe applied what the owner did in the portal? */
export function portalChangeApplied(
  expectation: PortalExpectation,
  sub: SubscriptionDetails | null | undefined,
): boolean {
  switch (expectation.action) {
    case 'update':
      // A specific target is known only for the confirm flow.
      return expectation.planKey
        ? sub?.plan_key === expectation.planKey &&
            sub?.interval === expectation.interval
        : true
    case 'cancel':
      return !!sub?.cancel_at
    default:
      return true
  }
}

export const PORTAL_DONE_MESSAGE: Record<PortalAction, string> = {
  update: 'Your plan has been updated.',
  cancel: 'Your subscription is set to cancel at the end of the period.',
  payment_method: 'Your payment method has been updated.',
}
